import createSelector from 'selectorator'
import { identity } from 'ramda'
import { RootState } from 'src/state/types'
import { BeneficiaryType, EasyPayBeneficiary, PartyBeneficiary } from 'types/finance'
import { adhocOnlyInvoiceTypes, NAMESPACE } from './constants'
import { TInvoice, TInvoiceNotification, TInvoiceState, TInvoiceTypeSummary, TMaybeId } from './types'

/**
 * Get a slice of the invoice API state
 * EG: getSlice('invoices')
 *
 * A path dependency rather than a `state => state.api.invoice` function, so each
 * selector below only recomputes when the slice it actually reads changes. Nothing here
 * reaches into another module, which is what lets every one of these be slice-scoped.
 */
const getSlice = <K extends keyof TInvoiceState>(key: K) =>
  createSelector<RootState, TInvoiceState[K]>([`api.${NAMESPACE}.${key}`], identity)

export const getInvoices = getSlice('invoices')

export const getInvoiceTypes = getSlice('invoiceTypes')

export const getInvoiceTypesForAdhocInvoices = getInvoiceTypes

export const getInvoiceTypesForNonAdhocInvoices = createSelector<RootState, TInvoiceTypeSummary[]>(
  [getInvoiceTypes],
  (invoiceTypes: TInvoiceTypeSummary[]) => invoiceTypes.filter(it => !adhocOnlyInvoiceTypes.includes(it.value)),
)

/**
 * Draft Invoices
 */
const getInvoicesByStatus = (status: string) =>
  createSelector<RootState, TInvoice[]>([getInvoices], (invoices: TInvoice[]) =>
    invoices.filter(i => i.tags?.status === status),
  )

export const getDraftInvoices = getInvoicesByStatus('Draft')

export const getRemovedDraftInvoices = getInvoicesByStatus('Removed Draft')

export const getDraftInvoicesCount = createSelector<RootState, number>(
  [getDraftInvoices],
  (draft: TInvoice[]) => draft.length,
)

const byLightStatus = (invoices: TInvoice[]) => (lightStatus: string, portfolioId?: TMaybeId) =>
  invoices
    .filter(inv => inv.tags?.lightStatus === lightStatus)
    .filter(inv => !portfolioId || inv.portfolioId === portfolioId)

export const getDraftInvoicesByLightStatus = createSelector<
  RootState,
  (lightStatus: string, portfolioId?: TMaybeId) => TInvoice[]
>([getDraftInvoices], byLightStatus)

export const getRemovedDraftInvoicesByLightStatus = createSelector<
  RootState,
  (lightStatus: string, portfolioId?: TMaybeId) => TInvoice[]
>([getRemovedDraftInvoices], byLightStatus)

export const getInvoiceById = createSelector<RootState, (id: TMaybeId) => TInvoice | undefined>(
  [getInvoices],
  (invoices: TInvoice[]) => (id: TMaybeId) => id ? invoices.find(invoice => invoice.id === id) : undefined,
)

export const isDraftRemoved = createSelector<RootState, (id: TMaybeId) => boolean>(
  [getInvoiceById],
  (invoiceById: (id: TMaybeId) => TInvoice | undefined) => (id: TMaybeId) =>
    invoiceById(id)?.tags?.status === 'Removed Draft',
)

/**
 * `Beneficiary` is discriminated on the *nested* `beneficiary.type`, which `Array.filter`
 * cannot narrow on its own — hence the explicit predicates.
 */
const isPartyBeneficiary = (b: TInvoice['beneficiaries'][number]): b is PartyBeneficiary =>
  b.beneficiary.type === BeneficiaryType.PartyBeneficiary

const isEasyPayBeneficiary = (b: TInvoice['beneficiaries'][number]): b is EasyPayBeneficiary =>
  b.beneficiary.type === BeneficiaryType.EasyPayBeneficiary

/**
 * @returns the customer's party id followed by the party id of every party beneficiary
 * on an invoice — the set of parties `ui/invoices` prefetches when a draft is opened.
 */
export const getInvoiceParties = createSelector<RootState, (id: TMaybeId) => string[]>(
  [getInvoiceById],
  (invoiceById: (id: TMaybeId) => TInvoice | undefined) => (id: TMaybeId) => {
    const invoice = invoiceById(id)
    if (!invoice) return []
    const customerPartyId = invoice.customer?.partyId
    return [
      ...(customerPartyId ? [customerPartyId] : []),
      ...invoice.beneficiaries.filter(isPartyBeneficiary).map(b => b.beneficiary.value.partyId),
    ]
  },
)

/**
 * @returns the EasyPay reference of every EasyPay beneficiary on an invoice
 */
export const getInvoiceEasyPayBeneficiaryReferences = createSelector<RootState, (id: TMaybeId) => string[]>(
  [getInvoiceById],
  (invoiceById: (id: TMaybeId) => TInvoice | undefined) => (id: TMaybeId) =>
    invoiceById(id)
      ?.beneficiaries.filter(isEasyPayBeneficiary)
      .map(b => b.beneficiary.value.easyPayReference) ?? [],
)

/**
 * `customer` is optional on the wire, so an invoice without one contributes its type
 * alone to the search string rather than throwing on `customer.name`.
 */
export const getFilteredInvoices = createSelector<RootState, (query: string) => TInvoice[]>(
  [getInvoices],
  (invoices: TInvoice[]) => (query: string) =>
    invoices.filter(invoice => `${invoice.customer?.name ?? ''}${invoice.invoiceType}`.toLowerCase().includes(query)),
)

export const getInvoiceTypeByValue = createSelector<RootState, (value?: string) => TInvoiceTypeSummary | false>(
  [getInvoiceTypes],
  (types: TInvoiceTypeSummary[]) => (value?: string) => types.find(t => t.value === value) || false,
)

export const getInvoiceTypeNameByValue = createSelector<RootState, (value?: string) => string>(
  [getInvoiceTypeByValue],
  (invoiceTypeByValue: (value?: string) => TInvoiceTypeSummary | false) => (value?: string) => {
    const invoiceType = invoiceTypeByValue(value)
    return invoiceType ? invoiceType.name : ''
  },
)

/**
 * Notifications
 */
export const getNotifications = getSlice('notifications')

export const getNotificationByName = createSelector<RootState, (name: string) => TInvoiceNotification | undefined>(
  [getNotifications],
  (notifications: TInvoiceNotification[]) => (name: string) => notifications.find(n => n.name === name),
)

export const getNotificationCountByName = createSelector<RootState, (name: string) => number>(
  [getNotificationByName],
  (notificationByName: (name: string) => TInvoiceNotification | undefined) => (name: string) =>
    notificationByName(name)?.count ?? 0,
)

export const getNotificationCountTotal = createSelector<RootState, number>(
  [getNotifications],
  (notifications: TInvoiceNotification[]) => notifications.reduce((total, n) => total + n.count, 0),
)

const getDraftInvoicesById = getSlice('draftInvoicesById')

export const getDraftInvoicesForPortfolio = createSelector<RootState, (id: TMaybeId) => string[]>(
  [getDraftInvoicesById],
  (draftInvoicesById: TInvoiceState['draftInvoicesById']) => (id: TMaybeId) => (id && draftInvoicesById[id]) || [],
)
